import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => Promise<void>;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchThemeSetting();

    // Subscribe to realtime changes for instant theme updates
    const channel = supabase
      .channel('theme-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.theme'
        },
        (payload) => {
          if (payload.new && 'setting_value' in payload.new) {
            const newValue = payload.new.setting_value as { dark_mode: boolean };
            setIsDarkMode(newValue?.dark_mode ?? false);
            applyTheme(newValue?.dark_mode ?? false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchThemeSetting = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'theme')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching theme:', error);
      }

      const darkMode = (data?.setting_value as { dark_mode: boolean })?.dark_mode ?? false;
      setIsDarkMode(darkMode);
      applyTheme(darkMode);
    } catch (error) {
      console.error('Error fetching theme setting:', error);
      applyTheme(false); // Default to light
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (darkMode: boolean) => {
    const html = document.documentElement;
    if (darkMode) {
      html.classList.add('dark');
      localStorage.setItem('site-theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('site-theme', 'light');
    }
  };

  const toggleTheme = async () => {
    const newDarkMode = !isDarkMode;
    
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          {
            setting_key: 'theme',
            setting_value: { dark_mode: newDarkMode }
          },
          { onConflict: 'setting_key' }
        );

      if (error) throw error;

      setIsDarkMode(newDarkMode);
      applyTheme(newDarkMode);
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
